// Trim Rebuy Smart Cart line-item properties to just "For: <first> <last>" (rebuy mode only).
//
// The MGT/PGT consent modal (assets/product-page-modal.js) writes several VISIBLE line-item
// properties — consent_first_name, consent_last_name, consent_date, date_of_birth, sex_at_birth, …
// They must stay visible so they reach checkout, but the cart drawer should show only who the test
// is "For", exactly like the old theme cart (snippets/cart-drawer.liquid: shows "For: first last",
// hides the rest). Rebuy's flyout instead renders every non-underscore property.
//
// Rebuy flyout DOM (confirmed live):
//   .rebuy-cart__flyout-item
//     .rebuy-cart__flyout-item-properties
//       .rebuy-cart__flyout-item-property.property-key-consent_first_name
//         .rebuy-cart__flyout-item-property-name / -separator / -value
//
// Approach: the static styling (hiding raw property rows, the For-line look, hiding the quantity
// selector for limit-1/consent items, the price loader spinner) lives in
// scss/sections/rebuy-smart-cart.scss — loaded globally in rebuy mode. This module only does the
// dynamic work: inject the "For: …" line, multiply the line price by quantity, and toggle the price
// loader class. A MutationObserver re-applies on Rebuy's flyout re-renders.

const FOR_LINE_CLASS = 'smart-cart-for-line';
const PRICE_LOADING_CLASS = 'scp-price-loading';
const UNIT_PRICE_CLASS = 'smart-cart-unit-price';
const DISCOUNT_ICON_CLASS = 'smart-cart-discount-icon';
// The old theme cart's icon-discount tag SVG (snippets/icon-discount.liquid), inlined.
const DISCOUNT_ICON_SVG = `<svg class="${DISCOUNT_ICON_CLASS}" viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></path></svg>`;

// Single source of truth for every Rebuy flyout class this module depends on. Rebuy owns this DOM;
// if they rename a class, update it here once instead of hunting 10+ inline strings. The dynamic
// per-property row (`property-key-<key>`) is built by propertyRow() since the key varies.
const SELECTORS = {
    cartRoot: '.rebuy-cart',
    item: '.rebuy-cart__flyout-item',
    properties: '.rebuy-cart__flyout-item-properties',
    propertyValue: '.rebuy-cart__flyout-item-property-value',
    price: '.rebuy-cart__flyout-item-price',
    saleMoney: '.rebuy-money.sale > span:not(.sr-only)',
    compareMoney: '.rebuy-money.compare-at > span:not(.sr-only)',
    allMoneySpans: '.rebuy-money > span:not(.sr-only)',
    title: '.rebuy-cart__flyout-item-product-title',
    qtyInput: '.rebuy-cart__flyout-item-quantity-widget-input',
    qtyButton: '.rebuy-cart__flyout-item-quantity-widget-button',
    discountMsg: '.rebuy-cart__flyout-item-discount-message',
    subtotal: '.rebuy-cart__flyout-subtotal-amount',
};

const propertyRow = (key) =>
    `.rebuy-cart__flyout-item-property.property-key-${key} ${SELECTORS.propertyValue}`;

// Per-element price-loader timers kept off the DOM node (WeakMap, not `el._scpLoadTimer`). When
// Rebuy replaces a node on re-render the old node is detached and GC'd with its timer entry — no
// orphaned timer that a later clearTimeout could never reach.
const loadTimers = new WeakMap();

const readPropValue = (item, key) => {
    // Scope to the property ROW, not the item root — Rebuy stamps EVERY `property-key-*` class onto
    // the .rebuy-cart__flyout-item root too, so a bare `.property-key-<key>` matches the root and
    // returns the item's first value (e.g. last name reads as the first name). Anchoring on
    // `.rebuy-cart__flyout-item-property` (via propertyRow) selects the specific row.
    const el = item.querySelector(propertyRow(key));
    return el ? el.textContent.trim() : '';
};

// Build/update the single "For: <first> <last>" line for one flyout item. Removes it when the item
// has no consent name (e.g. a plain supplement) so those items show no property block at all.
const formatItemProperties = (item) => {
    const container = item.querySelector(SELECTORS.properties);
    if (!container) return;

    const first = readPropValue(item, 'consent_first_name');
    const last = readPropValue(item, 'consent_last_name');
    let forLine = container.querySelector(`.${FOR_LINE_CLASS}`);

    if (first && last) {
        if (!forLine) {
            forLine = document.createElement('div');
            forLine.className = FOR_LINE_CLASS;
            container.appendChild(forLine);
        }
        const next = `<b>For:</b> ${first} ${last}`;
        if (forLine.innerHTML !== next) forLine.innerHTML = next;
    } else if (forLine) {
        forLine.remove();
    }
};

// Show the LINE price (unit × quantity) like the theme cart's item.final_line_price. Rebuy renders
// the UNIT price regardless of quantity, so at qty 2 it shows $58.65 instead of $117.30. We multiply
// each money amount in the line (sale + compare-at) by the line quantity.
const AMOUNT_RE = /^(\D*)([\d.,]+)(.*)$/;

// Idempotent: the unit amount is stamped on the span the first time we see it (data-scp-unit), so
// re-running (observer fires, our own write, rapid events) always computes line = unit × qty from
// the stored unit — never compounding. Rebuy re-renders price into fresh spans on a real change, so
// a new span re-stamps from the current (unit) value.
const formatAmountSpan = (span, qty) => {
    let unitText = span.dataset.scpUnit;
    if (unitText === undefined) {
        unitText = span.textContent.trim();
        span.dataset.scpUnit = unitText;
    }
    const match = unitText.match(AMOUNT_RE);
    if (!match) return;
    const value = parseFloat(match[2].replace(/,/g, '')) * qty;
    if (!Number.isFinite(value)) return;
    const formatted = value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    const next = `${match[1]}${formatted}${match[3]}`;
    if (span.textContent !== next) span.textContent = next;
};

// Capture the per-unit amount on a money span the first time we see it (data-scp-unit), before any
// qty multiply. Returns the stored unit text.
const stampUnit = (span) => {
    if (span.dataset.scpUnit === undefined) span.dataset.scpUnit = span.textContent.trim();
    return span.dataset.scpUnit;
};

// Per-item unit (each) price, placed directly UNDER THE PRODUCT TITLE — matching the old theme
// cart's line-item layout (title → unit price `<s>$69.00</s> $58.65` → …). The main
// `.rebuy-cart__flyout-item-price` keeps showing the line total (unit × qty) in its own column.
// Built from the stored per-unit amounts (data-scp-unit on the sale + compare spans).
const updateUnitPriceLine = (item, unitSale, unitCompare) => {
    const title = item.querySelector(SELECTORS.title);
    let unitLine = item.querySelector(`.${UNIT_PRICE_CLASS}`);

    if (!title || (!unitSale && !unitCompare)) {
        if (unitLine) unitLine.remove();
        return;
    }

    // Each-price with the per-unit compare-at strikethrough (e.g. <s>$69.00</s> $55.20). The
    // line-total column shows its own compare-at strikethrough too (Rebuy's native render).
    const html = `${unitCompare ? `<s>${unitCompare}</s>` : ''}<span>${unitSale || unitCompare}</span>`;
    if (!unitLine) {
        unitLine = document.createElement('div');
        unitLine.className = UNIT_PRICE_CLASS;
    }
    if (title.nextElementSibling !== unitLine) title.after(unitLine);
    if (unitLine.innerHTML !== html) unitLine.innerHTML = html;
};

const formatItemPrice = (item) => {
    const qtyInput = item.querySelector(SELECTORS.qtyInput);
    const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
    const priceEl = item.querySelector(SELECTORS.price);
    if (!priceEl) return;

    const sale = priceEl.querySelector(SELECTORS.saleMoney);
    const compare = priceEl.querySelector(SELECTORS.compareMoney);
    const spans = priceEl.querySelectorAll(SELECTORS.allMoneySpans);
    // Stamp the per-unit amounts first (so the under-title unit price is available even at qty 1).
    spans.forEach(stampUnit);
    // Unit price under the title (always — like the theme cart). Reuses the already-stamped values
    // off the sale/compare spans — no second DOM query.
    updateUnitPriceLine(item, sale && sale.dataset.scpUnit, compare && compare.dataset.scpUnit);
    // Main price element shows the line total (unit × qty). Always rewrite — including qty 1, where it
    // writes unit × 1 = unit. Skipping qty 1 left a stale multiplied total when quantity was decremented
    // back down (e.g. 2 → 1 kept showing $72): Rebuy only re-renders the price span for DISCOUNTED
    // lines, so non-discounted lines never got reset. formatAmountSpan reads data-scp-unit, so this is
    // idempotent.
    spans.forEach((span) => formatAmountSpan(span, qty));
};

// Prepend the tag icon to a per-item discount line (like the old theme cart's icon-discount), only
// when the discount message actually has text. Rebuy keeps the element in the DOM even when empty.
const formatItemDiscount = (item) => {
    const msg = item.querySelector(SELECTORS.discountMsg);
    if (!msg) return;
    const hasText = msg.textContent.trim().length > 0;
    const icon = msg.querySelector(`.${DISCOUNT_ICON_CLASS}`);
    if (hasText && !icon) {
        msg.insertAdjacentHTML('afterbegin', DISCOUNT_ICON_SVG);
    } else if (!hasText && icon) {
        icon.remove();
    }
};

const formatItem = (item) => {
    formatItemProperties(item);
    formatItemPrice(item);
    formatItemDiscount(item);
};

const formatAll = () => {
    document.querySelectorAll(SELECTORS.item).forEach(formatItem);
};

// Show a spinner over a line's price the moment its +/- button is clicked, until Rebuy re-renders
// the new price (cart.change). Mirrors the old theme cart's per-line loading state. Delegated on
// document so it survives Rebuy's flyout re-renders. A safety timeout clears the spinner if no
// cart.change arrives (e.g. clicking − at min quantity is a no-op).
const addLoader = (el) => {
    if (!el) return;
    el.classList.add(PRICE_LOADING_CLASS);
    clearTimeout(loadTimers.get(el));
    loadTimers.set(el, setTimeout(() => el.classList.remove(PRICE_LOADING_CLASS), 5000));
};

const showPriceLoader = (event) => {
    const button = event.target.closest(SELECTORS.qtyButton);
    if (!button) return;
    const item = button.closest(SELECTORS.item);
    if (!item) return;
    // Loader on the changed line's price AND the cart subtotal — both recalculate on a qty change.
    addLoader(item.querySelector(SELECTORS.price));
    addLoader(document.querySelector(SELECTORS.subtotal));
};

const clearPriceLoaders = () => {
    document.querySelectorAll(`.${PRICE_LOADING_CLASS}`).forEach((el) => {
        clearTimeout(loadTimers.get(el));
        el.classList.remove(PRICE_LOADING_CLASS);
    });
};

let observer = null;
let cartRoot = null;

// Disconnect while we mutate so our own injection doesn't retrigger the observer.
const apply = () => {
    if (observer) observer.disconnect();
    try {
        formatAll();
    } finally {
        if (observer && cartRoot) observer.observe(cartRoot, { childList: true, subtree: true });
    }
};

export const initRebuyCartProperties = () => {
    const start = () => {
        const root = document.querySelector(SELECTORS.cartRoot);
        if (!root) return false;
        cartRoot = root;
        observer = new MutationObserver(apply);
        observer.observe(root, { childList: true, subtree: true });
        apply();
        return true;
    };

    // The flyout (.rebuy-cart) only exists once the lazy Smart Cart module loads — poll until then.
    if (!start()) {
        const startedAt = Date.now();
        const timer = setInterval(() => {
            if (start() || Date.now() - startedAt > 10000) clearInterval(timer);
        }, 300);
    }

    // Reapply promptly on Rebuy's own cart/flyout lifecycle events too (belt-and-suspenders).
    ['rebuy:cart.change', 'rebuy:smartcart.ready', 'rebuy:smartcart.show'].forEach((ev) =>
        document.addEventListener(ev, apply)
    );

    // Per-line price loader on quantity +/- (cleared once the cart re-renders).
    document.addEventListener('click', showPriceLoader);
    document.addEventListener('rebuy:cart.change', clearPriceLoaders);
};

export default initRebuyCartProperties;
